import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Logout } from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { useAuth } from '../contexts/AuthContext';
import { InlineMessage } from '../components/ui';

interface GroupForm {
  organizationName: string;
  kind: 'buying_group' | 'multi_store';
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  website: string;
}

const readError = async (response: Response, fallback: string): Promise<string> => {
  const body = await response.json().catch(() => ({}));
  return body?.error?.message || body?.message || fallback;
};

/**
 * Onboarding step: sign up a buying group or head office (e.g. Fotosource
 * Canada). Creates the group's parent organization and a pending
 * `organizationGroups` application — a Motzu admin must approve it before it
 * can manage any member store, and member stores each consent on their own
 * afterward.
 *
 * Pre-shell page (no Layout/sidebar) and plain English, no `t()` — matches
 * its `OnboardingSelectTypePage` / setup-store / setup-vendor siblings.
 */
export const OnboardingSetupGroupPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);
  const [form, setForm] = useState<GroupForm>({
    organizationName: '',
    kind: 'buying_group',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    website: '',
  });

  const submitMutation = useMutation({
    mutationFn: async (input: GroupForm) => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/onboarding/complete-group', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          organizationName: input.organizationName,
          kind: input.kind,
          contactInfo: {
            contactName: input.contactName,
            contactEmail: input.contactEmail,
            contactPhone: input.contactPhone,
            website: input.website || undefined,
          },
        }),
      });
      if (!response.ok) {
        throw new Error(await readError(response, 'Failed to submit your application'));
      }
      return response.json();
    },
    onSuccess: (result) => {
      navigate(result?.data?.nextStep || '/onboarding/group-pending');
    },
  });

  const handleField = (field: keyof GroupForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Failed to log out:', err);
      setLoggingOut(false);
    }
  };

  const canSubmit =
    form.organizationName.trim().length > 0 &&
    form.contactName.trim().length > 0 &&
    form.contactEmail.trim().length > 0;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        pt: { xs: 6, md: 10 },
        pb: { xs: 6, md: 10 },
      }}
    >
      <Container maxWidth="sm">
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="text"
            startIcon={<Logout />}
            onClick={handleLogout}
            disabled={loggingOut}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            {loggingOut ? 'Signing out...' : 'Sign out'}
          </Button>
        </Box>

        <Box textAlign="center" mb={4}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
            Tell us about your group
          </Typography>
          <Typography variant="body1" color="text.secondary">
            A Motzu admin reviews every buying group and head office before it can manage member
            stores. Each store still joins and consents on its own — this does not give you
            access to any store's catalog or data.
          </Typography>
        </Box>

        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            {submitMutation.isError && (
              <InlineMessage severity="error">
                {submitMutation.error instanceof Error
                  ? submitMutation.error.message
                  : 'Failed to submit your application'}
              </InlineMessage>
            )}

            <Stack spacing={3}>
              <TextField
                fullWidth
                label="Group or Head Office Name"
                value={form.organizationName}
                onChange={(e) => handleField('organizationName', e.target.value)}
                required
                placeholder="e.g. Fotosource Canada"
              />

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  What best describes you?
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  value={form.kind}
                  onChange={(_e, value) => value && handleField('kind', value)}
                >
                  <ToggleButton value="buying_group" sx={{ textTransform: 'none' }}>
                    Buying group
                  </ToggleButton>
                  <ToggleButton value="multi_store" sx={{ textTransform: 'none' }}>
                    Multi-store head office
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <TextField
                fullWidth
                label="Your Name"
                value={form.contactName}
                onChange={(e) => handleField('contactName', e.target.value)}
                required
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={form.contactEmail}
                onChange={(e) => handleField('contactEmail', e.target.value)}
                required
              />
              <TextField
                fullWidth
                label="Phone"
                value={form.contactPhone}
                onChange={(e) => handleField('contactPhone', e.target.value)}
              />
              <TextField
                fullWidth
                label="Website"
                value={form.website}
                onChange={(e) => handleField('website', e.target.value)}
              />
            </Stack>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => submitMutation.mutate(form)}
                disabled={!canSubmit || submitMutation.isPending}
                sx={{ minWidth: 220, textTransform: 'none', fontWeight: 600 }}
              >
                {submitMutation.isPending ? 'Submitting...' : 'Submit for review'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default OnboardingSetupGroupPage;
